import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setTokens, clearTokens } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) setTokens(data.access_token, data.refresh_token);
    await fetchMe();
    return data;
  };

  const loginErp = async (erp_id, password) => {
    const { data } = await api.post("/auth/login-erp", { erp_id, password });
    if (data?.access_token) setTokens(data.access_token, data.refresh_token);
    await fetchMe();
    return data;
  };

  const requestOtp = async (mobile) => {
    const { data } = await api.post("/auth/otp/request", { mobile });
    return data;
  };

  const verifyOtp = async (mobile, code) => {
    const { data } = await api.post("/auth/otp/verify", { mobile, code });
    if (data?.access_token) setTokens(data.access_token, data.refresh_token);
    await fetchMe();
    return data;
  };

  const mobileLogin = async (mobile) => {
    const { data } = await api.post("/auth/mobile-login", { mobile });
    if (data?.access_token) setTokens(data.access_token, data.refresh_token);
    await fetchMe();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearTokens();
    setUser(null); setTenant(null);
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, loginErp, requestOtp, verifyOtp, mobileLogin, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  ho_admin: "HO Admin",
  national_manager: "National Manager",
  zonal_manager: "Zonal Manager",
  regional_manager: "Regional Manager",
  dse: "DSE",
};

// Field mobile-login roles (DSE + Zonal heads). These users get a restricted,
// mobile-first experience: certain tabs are hidden and the milestone engine is view-only.
export const FIELD_ROLES = ["dse", "zonal_manager"];
export const FIELD_HIDDEN_ROUTES = ["/dashboard", "/rewards", "/whatsapp", "/alerts"];
export const isFieldRole = (role) => FIELD_ROLES.includes(role);
